import {
  Box,
  Grid,
  makeStyles,
  FormikInputField,
  FormikSwitch,
  Typography,
  Flex,
  Collapse,
  Switch,
  DropdownSelect,
  CircularProgress,
  LANGUAGE_ENGLISH_NAMES,
  getEnabledTranslationLangs
} from '@paintscout/ui'
import type { MuiTheme as Theme, SupportedLang } from '@paintscout/ui'
import { useFlags } from 'shared/components/common/FlagsProvider'
import {
  FollowUpTable,
  Editor,
  PhoneNumberInput,
  FormikCheckbox,
  useClientOptions,
  useUser,
  useUserPermissions
} from '@paintscout/ui-business'
import type { EditorMethods, DropdownSelectOption } from '@paintscout/ui-business'
import type { FollowUp, ReminderOptionsInput } from '@paintscout/api'
import { AddressBookSelect, useQuote } from '@paintscout/react-quote'
import { getFeature, getObjectLabels } from '@paintscout/util/builder'
import { combineContexts, createQuoteContext, createUserContext } from '@paintscout/util/templater'
import Feature from 'src/components/common/Feature'
import { Field, FieldArray, Form, useFormikContext } from 'formik'
import type { EmailTemplate, UserDocument } from 'paintscout'
import React, { useMemo } from 'react'
import { useApolloClient } from '@apollo/react-hooks'
import { getDefaultEmail } from '../getDefaultEmail'
import { translateWorkOrderEmailContentWithLoadingState } from '../translateWorkOrderEmailContent'
import { isAddressBookGroupOption } from '../../SendQuoteDialogNext/sendQuoteFormShared'
const useStyles = makeStyles((theme: Theme) => ({
  root: {
    // display: 'flex',
    // flexDirection: 'column',
    width: '100%',
    padding: theme.spacing()
  },
  bodyLabel: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  bodySublabel: {
    marginLeft: theme.spacing()
  },
  sms: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  smsTo: {
    flexGrow: 1
  },
  smsSwitch: {
    marginRight: theme.spacing(),
    marginTop: 24
  },
  showToggle: {
    whiteSpace: 'nowrap',
    cursor: 'pointer'
  },
  translatingWrapper: {
    position: 'relative'
  },
  translatingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255, 255, 255, 0.6)',
    zIndex: 2,
    // The overlay only signals progress; it shouldn't block interaction with the fields.
    pointerEvents: 'none'
  }
}))

export interface SendQuoteFormValues {
  to?: string[]
  from?: {
    name: string
    email: string
  }
  subject: string
  body: string
  attachPdf?: boolean
  sms?: {
    to?: string
    enabled?: boolean
    /** Editable SMS text; defaults to the subject. The estimate link is appended automatically. */
    message?: string
  }
  silentQuoteLink?: boolean
  signature: string // not editable
  followUps?: FollowUp[]
  followUpsDisabled?: boolean
  view?: 'quote' | 'work-order'
  reminderOptions?: ReminderOptionsInput
  /** Target language for the work-order link. Undefined = English (no `lang` param sent). */
  lang?: string
}

export interface SendQuoteFormProps extends React.HTMLProps<HTMLFormElement> {
  bouncedEmailAddresses?: string[]
  emailSelector?: React.ReactNode
  templateSelector?: React.ReactNode
  emailName?: 'To Client' | 'To Staff'
  editorRef?: React.Ref<EditorMethods>
  /** The raw (not-yet-templated) content currently backing the work-order "To Client" email. */
  workOrderEmailTemplate?: EmailTemplate
  /** True while the subject/body are being (re-)translated -- shows a loading overlay over those fields. */
  translating?: boolean
  onTranslatingChange?: (translating: boolean) => void
}
const SendQuoteForm = React.forwardRef<HTMLFormElement, SendQuoteFormProps>(
  (
    {
      bouncedEmailAddresses,
      emailSelector,
      editorRef,
      templateSelector,
      disabled,
      emailName,
      workOrderEmailTemplate,
      translating,
      onTranslatingChange,
      ...props
    },
    ref
  ) => {
    const classes = useStyles({})
    const { options } = useClientOptions()
    const { quote } = useQuote()
    const { user, isTrial } = useUser()
    const apolloClient = useApolloClient()
    const { permissionToViewFollowUps } = useUserPermissions()
    const objectLabels = getObjectLabels({ options })
    const hasSettingsCommunications = getFeature({
      options,
      path: 'settingsCommunications.enabled'
    })
    const templateContext = useMemo(
      () =>
        combineContexts(
          createQuoteContext({ quote, options }),
          createUserContext({ user: user.user_metadata as UserDocument })
        ),
      [quote, options]
    )

    const templateTags = Object.keys(templateContext)
      .map((key) => {
        let label = null
        switch (key) {
          case 'quoteNumber':
            label = `${quote.is_invoice ? 'Invoice' : objectLabels.quote.value} Number`
            break
          case 'intExt':
            label = `${objectLabels.quote.value} Type`
            break
          case 'clientName':
            label = `Client Full Name`
            break
          case 'clientFirstName':
            label = `Client First Name`
            break
          case 'clientLastName':
            label = `Client Last Name`
            break
          case 'clientSecondaryFirstName':
            label = 'Client Secondary First Name'
            break
          case 'clientSecondaryLastName':
            label = 'Client Secondary Last Name'
            break
          case 'jobIdentifier':
            label = `Job Identifier`
            break
          case 'address':
            label = `Client/Job Address`
            break
          case 'city':
            label = `Client/Job City`
            break
          case 'state':
            label = `Client/Job State`
            break
          case 'zip':
            label = `Client/Job Zip Code`
            break
          case 'estimatorInfo':
            label = `Estimator Info`
            break
          case 'estimatorName':
            label = `Estimator Name`
            break
          case 'estimatorFirstName':
            label = `Estimator First Name`
            break
          case 'estimatorEmail':
            label = `Estimator Email`
            break
          case 'estimatorPhoneNumber':
            label = `Estimator Phone`
            break
          case 'companyName':
            label = 'Company Name'
            break
          case 'companyAddress':
            label = 'Company Address'
            break
          case 'companyCity':
            label = 'Company City'
            break
          case 'companyProv':
            label = 'Company State/Province'
            break
          case 'companyCountry':
            label = 'Company Country'
            break
          case 'companyPostal':
            label = 'Company ZIP/Postal Code'
            break
          case 'companyPhone':
            label = 'Company Phone Number'
            break
        }
        return {
          key,
          label
        }
      })
      // remove any tags with no label or context value
      .filter((tag) => !!tag.label && !!templateContext[tag.key])

    const { values, setFieldValue, errors } = useFormikContext<SendQuoteFormValues>()

    // Translation language selection is gated behind the `translations` feature flag:
    // availableLangs is empty (and the selector hidden) for companies without it enabled.
    const { translations } = useFlags()
    const availableLangs = getEnabledTranslationLangs(translations)
    const showLangSelect = values.view === 'work-order' && availableLangs.length > 0
    const englishLangOption: DropdownSelectOption = { label: LANGUAGE_ENGLISH_NAMES['en'], value: 'en' }
    const langOptions: DropdownSelectOption[] = [
      englishLangOption,
      ...availableLangs.map((lang) => ({ label: LANGUAGE_ENGLISH_NAMES[lang as SupportedLang], value: lang }))
    ]
    const selectedLangOption = langOptions.find((option) => option.value === values.lang) ?? englishLangOption

    return (
      <Form ref={ref} className={classes.root} {...(props as any)}>
        <Grid container spacing={1} direction="column">
          {!!emailSelector && (
            <Grid item xs={12} className={'send-quote-email-type'}>
              {emailSelector}
            </Grid>
          )}
          {!!templateSelector && (
            <Grid item xs={12} className={'send-quote-template'}>
              {templateSelector}
            </Grid>
          )}
          <Grid item xs={12} className={'send-quote-to'}>
            <FieldArray
              name={'to'}
              render={(arrayHelpers) => (
                <AddressBookSelect
                  label="To"
                  className="send-quote-to-field"
                  disabled={disabled || isTrial}
                  bouncedValues={bouncedEmailAddresses ? mapAddressesToOptions(bouncedEmailAddresses) : null}
                  value={mapAddressesToOptions(arrayHelpers.form.values.to)}
                  required
                  error={!!errors?.to && (!values.to || values.to.length === 0)}
                  searchable
                  showErrorBeforeValidation
                  onChange={(option: DropdownSelectOption[]) => {
                    arrayHelpers.form.setFieldValue(
                      'to',
                      option.filter((o) => !isAddressBookGroupOption(o)).map((o) => o.label)
                    )
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} className={'send-quote-sms'}>
            <Feature required={{ 'sms.enabled': true }}>
              <>
                <div className={classes.sms}>
                  <Field
                    label="Send Text Message"
                    name="sms.to"
                    classes={{ root: classes.smsTo }}
                    disabled={disabled || !values.sms?.enabled || isTrial}
                    component={FormikInputField}
                    inputComponent={PhoneNumberInput}
                    inputProps={{
                      separator: options?.options?.phoneNumbers?.separator,
                      internationalMode: options?.options?.internationalMode ?? false
                    }}
                    fullWidth
                    autoSelect
                  />
                  <Field name="sms.enabled" component={FormikSwitch} classes={{ root: classes.smsSwitch }} />
                </div>
                {values.sms?.enabled && (
                  <div style={{ paddingTop: '4px' }}>
                    <Typography variant="subtitle2">
                      * Please note that text messages only include the subject line and link to the estimate
                    </Typography>
                  </div>
                )}
                {errors?.sms?.to && <ErrorMessage>{errors.sms?.to}</ErrorMessage>}
              </>
            </Feature>
          </Grid>
          <Grid item xs={12} className={'send-quote-from'}>
            <Field component={FormikInputField} label="From" name="from.name" disabled fullWidth />
          </Grid>
          <Grid item xs={12} className={`send-quote-subject-body ${classes.translatingWrapper}`}>
            {translating && (
              <div className={classes.translatingOverlay} data-testid="send-quote-content-translating">
                <CircularProgress size={24} />
              </div>
            )}
            <Grid item xs={12} className={'send-quote-subject'}>
              <Field
                component={FormikInputField}
                label="Subject"
                disabled={disabled || translating}
                name="subject"
                fullWidth
              />
              {errors?.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
            </Grid>
            <Grid item xs={12} className={'send-quote-body'}>
              <Editor
                content={values.body}
                label={'Body'}
                required={false}
                disabled={disabled || translating}
                fullWidth
                ref={editorRef}
                onChange={(value) => setFieldValue('body', value)}
                debounce={true}
                templateContext={templateContext}
                toolbar={{
                  template: {
                    tooltip: 'Insert Placeholder',
                    tags: templateTags
                  },
                  color: false,
                  highlight: false
                }}
              />
              <Typography classes={{ root: classes.bodySublabel }} variant={'subtitle2'}>
                * A button with a link to the estimate will be added to your email
              </Typography>
              {errors?.body && <ErrorMessage>{errors.body}</ErrorMessage>}
            </Grid>
          </Grid>
          <Feature required={{ 'quotes.emails.attachPdf': true }}>
            <Grid item xs={12} className={'send-quote-pdf'}>
              <Field component={FormikCheckbox} disabled={disabled} label="Attach PDF" name="attachPdf" />
            </Grid>
          </Feature>
          {showLangSelect && (
            <Grid item xs={12} className={'send-quote-lang'}>
              <DropdownSelect
                variant="single"
                label="Work Order Language"
                options={langOptions}
                value={selectedLangOption}
                disabled={disabled}
                onChange={async (option) => {
                  const value = (option as DropdownSelectOption).value
                  // English = no lang param on the link, so store undefined.
                  const newLang = value === 'en' ? undefined : value
                  setFieldValue('lang', newLang)

                  // Render the work-order email itself in the selected language, using whichever
                  // template the user actually has selected (see workOrderEmailTemplate) rather
                  // than always re-deriving the fixed default, then live-translate that rendered
                  // content -- including a company's own custom copy, not just the one hardcoded
                  // default string -- so the crew member gets the email in their language.
                  const { subject: rawSubject, body: rawBody } = getDefaultEmail({
                    user,
                    quote,
                    options,
                    autofillTo: false,
                    emailTemplate: workOrderEmailTemplate
                  })
                  const { subject: newSubject, body: newBody } = await translateWorkOrderEmailContentWithLoadingState({
                    apolloClient,
                    subject: rawSubject,
                    body: rawBody,
                    lang: newLang,
                    setLoading: (loading) => onTranslatingChange?.(loading)
                  })
                  setFieldValue('subject', newSubject)
                  setFieldValue('body', newBody)
                  ;(editorRef as React.RefObject<EditorMethods>)?.current?.setHTML(newBody)
                }}
              />
            </Grid>
          )}
          {emailName === 'To Client' && values.view === 'quote' && (
            <Grid item xs={12} className={'send-quote-pdf'}>
              <Field
                component={FormikCheckbox}
                disabled={disabled}
                label="Send Without View Tracking"
                name="silentQuoteLink"
              />
            </Grid>
          )}
          <Feature required={{ 'quotes.followUps.enabled': true }}>
            {/* values.followUps will be undefined for 'To Staff' emails */}
            {values.followUps && permissionToViewFollowUps && (
              <Grid item xs={12} className={'send-quote-follow-ups'}>
                <Box mt={2}>
                  <Flex justify="space-between" align="center">
                    <Box>
                      <Typography variant={'h4'}>Follow-Ups</Typography>
                    </Box>
                    <Box>
                      <Switch
                        inputProps={{
                          'data-testid': 'toggle-follow-ups'
                        }}
                        checked={!values.followUpsDisabled}
                        onChange={(_, checked) => {
                          setFieldValue('followUpsDisabled', !checked)
                        }}
                      />
                    </Box>
                  </Flex>

                  <Collapse show={!values.followUpsDisabled} scroll>
                    <Box mt={1}>
                      {values.followUps.length > 0 ? (
                        <FollowUpTable
                          quoteView
                          quote={quote}
                          followUps={values.followUps}
                          controls={{ toggle: true }}
                          onAction={(action, followUp) => {
                            const index = values.followUps.findIndex((f) => f.key === followUp.key)
                            if (action === 'toggleSendEmail') {
                              setFieldValue(`followUps[${index}].sendEmail`, !followUp.sendEmail)
                            } else if (action === 'toggleSendText') {
                              setFieldValue(`followUps[${index}].sendText`, !followUp.sendText)
                            }
                          }}
                        />
                      ) : (
                        <Typography align="center" variant="subtitle1">
                          You can configure Follow-Ups in your {hasSettingsCommunications ? 'Settings' : 'Profile'}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              </Grid>
            )}
          </Feature>
        </Grid>
      </Form>
    )
  }
)
SendQuoteForm.displayName = 'SendQuoteForm'

function ErrorMessage({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="subtitle2" color="error">
      {children}
    </Typography>
  )
}

function mapAddressesToOptions(to: string[]) {
  return to.map((address) => ({
    value: address,
    label: address
  }))
}

export default SendQuoteForm
